'use client';
import React, { useState } from 'react';
import { useEditor, Patch } from './EditorContext';
import { MediaPickerModal } from './MediaPickerModal';

type Props = {
  id: string;
  src: string;
  alt?: string;
  buildPatch: (newSrc: string) => Patch;
  className?: string;
  style?: React.CSSProperties;
  renderAsBackground?: (src: string) => React.ReactNode;
};

export function EditableImage({ id, src, alt, buildPatch, className, style, renderAsBackground }: Props) {
  const { editing, setPatch, clearPatch } = useEditor();
  const [currentSrc, setCurrentSrc] = useState(src);
  const [drag, setDrag] = useState(false);
  const [open, setOpen] = useState(false);

  const apply = (newSrc: string) => {
    setCurrentSrc(newSrc);
    if (newSrc === src) clearPatch(id);
    else setPatch(id, buildPatch(newSrc));
    setOpen(false);
  };

  const onDrop = async (e: React.DragEvent) => {
    if (!editing) return;
    e.preventDefault();
    setDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append('file', f);
    try {
      const res = await fetch('/api/upload', { method: 'POST', body: fd });
      if (!res.ok) throw new Error(await res.text());
      const { url } = await res.json();
      apply(url);
    } catch (e: any) {
      alert(e?.message || 'Upload failed');
    }
  };

  const overlay = editing ? (
    <span className="onpage-image-overlay" aria-hidden="true">
      {drag ? 'Drop image to upload' : 'Click to change image'}
    </span>
  ) : null;

  const wrapProps = {
    onClick: editing ? () => setOpen(true) : undefined,
    onDragOver: (e: React.DragEvent) => { if (editing) { e.preventDefault(); setDrag(true); } },
    onDragLeave: () => setDrag(false),
    onDrop,
  };

  return (
    <>
      {renderAsBackground ? (
        <div
          className={['onpage-editable-image', editing && 'onpage-editable-image--on', className].filter(Boolean).join(' ')}
          style={style}
          {...wrapProps}
        >
          {renderAsBackground(currentSrc)}
          {overlay}
        </div>
      ) : (
        <span
          className={['onpage-editable-image', editing && 'onpage-editable-image--on', className].filter(Boolean).join(' ')}
          style={style}
          {...wrapProps}
        >
          {currentSrc ? <img src={currentSrc} alt={alt || ''} style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }} /> : null}
          {overlay}
        </span>
      )}
      <MediaPickerModal
        open={open}
        currentSrc={currentSrc}
        accept="image/*"
        onConfirm={apply}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
